import { Injectable } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Utils } from '../../../../../../common/js/Utils';
import { Observable, of, timer, pipe, from } from 'rxjs';
import { debounceTime, map, switchMap, take } from 'rxjs/operators';
import {
  CPConvertibleTermAgeRanges,
  FaceAmountLimit,
} from '../../../../../../coverpath-common/angular/services/validation/CPConvertibleTermAgeRanges';
import { ValidationService } from '../../../../../../coverpath-common/angular/services/validation/validation.service';
import { Assumptions } from '../../../../../../common/js/static/Assumptions';
import {
  LowFaceWholeLife,
  MinimumFaceIncreaseInterval,
  Product,
  productEligibileForFaceIncreaseIllustration,
  RiderConstants,
  RiderType,
} from '../../../../../../common/js/static/Product';
import { StateSpecification } from '../../../../../../coverpath-common/classes/wholelife/basicIllustration/templates/common/StateSpecification';
import { Controller } from '../../../Controller';
import { AgentAmorphicService } from '../../../services/amorphic/amorphic.service';
import { AgentLicenseService } from '../../../services/cache/agent-license.service';
import { ClientInformation, QuoteForm, TermQuote, WholeLifeQuote } from './QuoteForm';
import {
  CIRValidationUtils,
  GIRValidationUtils,
  RTRValidationUtils,
  WholeLifeValidationUtils,
} from '@hit/validation-utils';
import { PremiumRequest } from '../../../../../../common/js/productService/ProductServiceClient';
import { RateClasses } from '../../../../../../common/js/static/RateClasses';
import { CommonUtils } from '../../../../../../common/js/static/CommonUtils';
import { ProductType } from '../../../../../../common/js/interfaces/CommonTypes';
import { Exchange1035FundingPurpose } from '../../../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { debounce, isArray } from 'lodash';
import { TotalPremiumService } from './total-premium.service';
import { Agent } from '../../../../../../common/js/customer/Agent';
import { POLICY_TYPES } from '../../../../../../common/js/constants/policy';
import { CoverpathUtils } from '../../../../../../coverpath-common/js/static/CoverpathUtils';
import { ValidationStatic } from '../../../../../../coverpath-common/angular/services/validation/validation-static';

@Injectable()
export class ValidatorsService {
  controller: Controller;
  debouncedFetchPremium: (request) => void;
  debouncedWholeAlirValidator;
  debouncedJointAlirValidator;
  debouncedWholeCirValidator;
  debouncedJointCirValidator;

  constructor(
    private amorphicService: AgentAmorphicService,
    private validationService: ValidationService,
    private agentLicenseService: AgentLicenseService,
    private totalPremiumService: TotalPremiumService
  ) {
    this.controller = this.amorphicService.controller as unknown as Controller;
    this.debouncedFetchPremium = debounce(this.fetchPremiumAndValidateAmount, 500);
    this.debouncedWholeAlirValidator = debounce(this.wholeAlirValidator, 200);
    this.debouncedJointAlirValidator = debounce(this.jointAlirValidator, 200);
    this.debouncedWholeCirValidator = debounce(this.wholeCirValidator, 200);
    this.debouncedJointCirValidator = debounce(this.jointCirValidator, 200);
  }

  private fetchPremiumAndValidateAmount(request): void {
    const girRiderAmount = request.wholeLifeQuote.riders.gir.amount;
    const rtrRiderAmount = request.wholeLifeQuote.riders.rtr.amount;

    const cirRiderAmount = request.wholeLifeQuote.riders.cir.amount;
    const cirBenefitDisbursementMethod = request.wholeLifeQuote.riders.cir.benefitDisbursementMethod;
    const cirMonthlyIncreaseOption = request.wholeLifeQuote.riders.cir.monthlyIncreaseOption;

    const policyInfo: any = [
      request.clientInformation.dob,
      request.clientInformation.gender,
      request.clientInformation.healthClass,
      request.clientInformation.contractState,
      request.wholeLifeQuote.product,
      request.wholeLifeQuote.faceAmount,
      request.wholeLifeQuote.paymentFrequency,
      request.wholeLifeQuote.waiverOfPremium,
      girRiderAmount ? request.wholeLifeQuote.riders.gir.enabled : false,
      girRiderAmount ? girRiderAmount : 0,
      rtrRiderAmount ? request.wholeLifeQuote.riders.rtr.enabled : false,
      rtrRiderAmount ? rtrRiderAmount : 0,
      cirRiderAmount ? request.wholeLifeQuote.riders.cir.enabled : false,
      cirRiderAmount ? cirRiderAmount : 0,
      cirRiderAmount ? cirBenefitDisbursementMethod : null,
      cirRiderAmount ? cirMonthlyIncreaseOption : null,
      request.clientInformation.healthClass,
    ];
    this.fetchPremium(policyInfo)
      .then((ret) => {
        const annualBasePremium: number = ret ? ret.annualBase : 0;
        request.validateAmountFn(
          annualBasePremium,
          request.wholeLifeQuote,
          request.fieldControl,
          request.quoteFaceAmount,
          request.wholeLifeGlobalErrorControl,
          request.clientInformation,
          request.maxFaceAllowed
        );
      })
      .catch(() => null);
  }

  dobValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const dob: Date = control.value;

    if (dob) {
      if (
        CommonUtils.isYoungerThanAge(dob, Product.productSpecs.AllProductAge.minAge) ||
        CommonUtils.isOlderThanOrEqualToAge(dob, Product.productSpecs.AllProductAge.maxAge)
      ) {
        return {
          dob: { min: 0, max: 64 },
        };
      }
    }
    return null;
  };

  private isWithinAgeRange(age: number, lowerBound: number, upperBound: number): boolean {
    return age >= lowerBound && age <= upperBound;
  }

  private _wholeProductValidator(
    clientInformation: ClientInformation,
    productControl: AbstractControl
  ): ValidationErrors | null {
    const dob: Date = clientInformation.dob;
    const contractState: string = clientInformation.contractState;
    const healthClass: string = clientInformation.healthClass;
    const product = productControl.value;

    if (!product || !dob) {
      // pre-requisite for running validation
      return null;
    }

    const min = Product.products[product].productSpecs.minAge;
    const max = Product.products[product].productSpecs.maxAge;
    const actualAge = Utils.getActualAge(dob);
    const isJuvenile = ValidationService.isJuvenile(dob);

    const hasValidAge = isJuvenile
      ? ValidationService.isValidJuvenileDOB(dob, 18).result
      : CommonUtils.isOlderThanOrEqualToAge(dob, min) && CommonUtils.isYoungerThanAge(dob, max);

    if (isJuvenile && contractState === 'WA' && this.isWithinAgeRange(actualAge, 15, 17)) {
      this.deleteError(productControl, 'dob');
      this.deleteError(productControl, 'juvenileDOB');
      this.addError(productControl, { juvenileDOB15to17: true });
    } else {
      this.deleteError(productControl, 'juvenileDOB15to17');
    }

    if (hasValidAge) {
      this.deleteError(productControl, 'dob');
      this.deleteError(productControl, 'juvenileDOB');
    } else {
      if (isJuvenile) {
        this.deleteError(productControl, 'dob');
        this.addError(productControl, { juvenileDOB: true });
      } else {
        this.deleteError(productControl, 'juvenileDOB');
        this.addError(productControl, { dob: { min, max } });
      }
    }

    return null;
  }

  wholeProductValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { clientInformation }: QuoteForm = control.value;
    const productControl = control.get('wholeLifeQuote.product');
    return this._wholeProductValidator(clientInformation, productControl);
  };
  jointAppWholeProductValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { clientInformation }: QuoteForm = control.value;
    const productControl = control.get('jointAppQuote.wholeLifeQuote.product');
    return this._wholeProductValidator(clientInformation, productControl);
  };

  private _wholeMinFaceAmountValidator(
    clientInformation: ClientInformation,
    wholeLifeQuote: WholeLifeQuote,
    faceAmountControl: AbstractControl,
    productControl: AbstractControl,
    isJoint = false
  ): ValidationErrors | null {
    if (faceAmountControl.value < LowFaceWholeLife.minFace) {
      this.addError(faceAmountControl, {
        minFaceAmount: { min: LowFaceWholeLife.minFace, isPolicyWLandLowFace: 'NA' },
      });
      return null;
    }

    const productType = ProductType.WHOLE;
    const product = productControl.value;
    const rtrAmount = wholeLifeQuote.riders?.rtr?.enabled ? wholeLifeQuote.riders.rtr.amount : 0;
    const faceAmount = faceAmountControl.value + rtrAmount;
    const isAmendment = faceAmountControl.root.get('isAmendment').value;
    let isPolicyWLandLowFace: 'NA' | 'true' | 'false' = 'NA'; // If no max face we are in quote page, low face isn't applicable
    if (faceAmount && LowFaceWholeLife.availableProducts.includes(product)) {
      isPolicyWLandLowFace = faceAmount < LowFaceWholeLife.maxFace ? 'true' : 'false';
    }
    if (isAmendment) {
      const originalRtrAmount = Utils.get(this, 'controller.customer.application.primaryPolicy.rtrRiderAmount') ?? 0;
      const originalFace =
        Utils.get(this, 'controller.customer.application.primaryPolicy.faceAmount') + originalRtrAmount;
      if (originalFace && LowFaceWholeLife.availableProducts.includes(product)) {
        isPolicyWLandLowFace = originalFace < LowFaceWholeLife.maxFace ? 'true' : 'false';
      }

      //If Rate class standard or lower, allow amending high faceAmount(>50k) policy to low faceAmount(<50k)
      const finalRateClass = wholeLifeQuote.healthClass && RateClasses[wholeLifeQuote.healthClass].code;
      if (finalRateClass && finalRateClass >= RateClasses.STANDARD.code) {
        isPolicyWLandLowFace = 'NA';
      }
    }

    const min = ValidationService.isMinimumFaceAmountMet({
      isJoint,
      healthClass: clientInformation.healthClass,
      productType,
      product,
      faceAmount,
      lowFaceWholeLifeEnabled: !!this.controller.lowFaceWholeLifeEnabled,
      isPolicyWLandLowFace,
      dob: clientInformation.dob,
    });

    if (min === true) {
      this.deleteError(faceAmountControl, 'minFaceAmount');
    } else {
      this.addError(faceAmountControl, { minFaceAmount: { min, isPolicyWLandLowFace } });
    }
    return null;
  }

  wholeMinFaceAmountValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { clientInformation, wholeLifeQuote }: QuoteForm = control.value;
    const faceAmountControl = control.get('wholeLifeQuote.faceAmount');
    const productControl = control.get('wholeLifeQuote.product');
    // Required because on form change a JS error in thrown on product.value when product isn't selected yet.
    if (productControl.value) {
      return this._wholeMinFaceAmountValidator(
        clientInformation,
        wholeLifeQuote,
        faceAmountControl,
        productControl,
        false
      );
    }
  };

  jointAppWholeMinFaceAmountValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const {
      clientInformation,
      jointAppQuote: { wholeLifeQuote },
    }: QuoteForm = control.value;
    const faceAmountControl = control.get('jointAppQuote.wholeLifeQuote.faceAmount');
    const productControl = control.get('jointAppQuote.wholeLifeQuote.product');
    // Required because on form change a JS error in thrown on product.value when product isn't selected yet.
    if (productControl.value) {
      return this._wholeMinFaceAmountValidator(
        clientInformation,
        wholeLifeQuote,
        faceAmountControl,
        productControl,
        true
      );
    }
  };

  private _wholeMaxFaceAmountValidator(
    clientInformation: ClientInformation,
    wholeLifeQuote: WholeLifeQuote,
    faceAmountControl: AbstractControl,
    maxFaceAmountControl: AbstractControl,
    isJoint: boolean
  ): ValidationErrors | null {
    const isAmendment = faceAmountControl.root.get('isAmendment').value;
    const faceAmount = faceAmountControl.value;
    const rtrAmount = wholeLifeQuote.riders?.rtr?.enabled ? wholeLifeQuote.riders.rtr.amount : 0;
    const maxFaceAmount = isAmendment && maxFaceAmountControl?.value ? maxFaceAmountControl.value - rtrAmount : null;
    const originalRtrAmount = Utils.get(this, 'controller.customer.application.primaryPolicy.rtrRiderAmount') ?? 0;
    const originalFace =
      Utils.get(this, 'controller.customer.application.primaryPolicy.faceAmount') + originalRtrAmount;
    let isPolicyWLandLowFace: 'NA' | 'true' | 'false' = 'NA';
    if (isAmendment && originalFace) {
      isPolicyWLandLowFace = originalFace < LowFaceWholeLife.maxFace ? 'true' : 'false';
    }
    const isUsCitizen: boolean = ValidatorsService.isUSCitizen(clientInformation.citizenship);
    const product = wholeLifeQuote.product;
    if (faceAmount && product) {
      const max = ValidationStatic.isMaximumFaceAmountMetForWhole({
        dob: clientInformation.dob,
        product: product,
        faceAmount,
        maxFaceAmount,
        isPolicyWLandLowFace,
        isUsCitizen,
      });

      const minFaceAmountAllowed = ValidationStatic.getMinimumFaceAmountAllowed({
        isJoint,
        productType: ProductType.WHOLE,
        product: product,
        healthClass: clientInformation.healthClass,
        lowFaceWholeLifeEnabled: !!this.controller.lowFaceWholeLifeEnabled,
        isPolicyWLandLowFace,
        dob: clientInformation.dob,
      });

      if (max !== true && max >= minFaceAmountAllowed) {
        this.addError(faceAmountControl, { maxFaceAmount: { max, isPolicyWLandLowFace } });
        return null;
      }
    }

    this.deleteError(faceAmountControl, 'maxFaceAmount');
    return null;
  }

  wholeMaxFaceAmountValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const { clientInformation, wholeLifeQuote } = control.getRawValue();
    const faceAmountControl = control.get('wholeLifeQuote.faceAmount');
    const maxFaceAmountControl = control.get('wholeLifeQuote.maxFaceAmount');
    return this._wholeMaxFaceAmountValidator(
      clientInformation,
      wholeLifeQuote,
      faceAmountControl,
      maxFaceAmountControl,
      false
    );
  };

  // this case is only used for InForce to determine that the faceamount is only a decrease of the current
  wholeMaxFaceAmountValidatorInForce(
    control: AbstractControl,
    originalFaceAmount: number,
    enableWLFaceIncreaseIllustration: boolean
  ): ValidatorFn {
    return (control: FormGroup): ValidationErrors | null => {
      const faceAmountControl = control.get('faceAmount');
      const productControl = control.get('product');
      const dobControl = control.get('dob');
      const healthClassControl = control.get('healthClass');
      const isUsCitizenControl = control.get('isUsCitizen');
      const maxFaceAmountControl = control.get('maxFaceAmount');
      return this._wholeMaxFaceAmountValidatorInForce(
        faceAmountControl,
        productControl,
        dobControl,
        healthClassControl,
        isUsCitizenControl,
        maxFaceAmountControl,
        originalFaceAmount,
        false,
        enableWLFaceIncreaseIllustration
      );
    };
  }

  private _wholeMaxFaceAmountValidatorInForce(
    faceAmountControl: AbstractControl,
    productControl: AbstractControl,
    dobControl: AbstractControl,
    healthClassControl: AbstractControl,
    isUsCitizenControl: AbstractControl,
    maxFaceAmountControl: AbstractControl,
    originalFaceAmount: number,
    isJoint: boolean,
    enableWLFaceIncreaseIllustration: boolean
  ): ValidationErrors | null {
    const faceAmount = faceAmountControl.value;
    const product = productControl.value;
    const dob = dobControl.value;
    const healthClass = healthClassControl.value;
    const isUsCitizen = isUsCitizenControl.value;
    const maxFaceAmount = maxFaceAmountControl && maxFaceAmountControl.value ? maxFaceAmountControl.value : null;
    const isPolicyWLandLowFace = 'NA';
    if (!enableWLFaceIncreaseIllustration && originalFaceAmount < faceAmount) {
      const max = originalFaceAmount;
      this.addError(faceAmountControl, { maxFaceAmount: { max } });
      return null;
    }
    if (faceAmount && product) {
      const max = ValidationStatic.isMaximumFaceAmountMetForWhole({
        dob: dob,
        product: product,
        faceAmount,
        maxFaceAmount,
        isPolicyWLandLowFace,
        isUsCitizen,
      });

      const minFaceAmountAllowed = ValidationStatic.getMinimumFaceAmountAllowed({
        isJoint,
        productType: ProductType.WHOLE,
        product: product,
        healthClass: healthClass,
        lowFaceWholeLifeEnabled: !!this.controller.lowFaceWholeLifeEnabled,
        isPolicyWLandLowFace,
        dob: dob,
      });

      if (max !== true && max >= minFaceAmountAllowed) {
        this.addError(faceAmountControl, { maxFaceAmount: { max, isPolicyWLandLowFace } });
        return null;
      }
    }
    this.deleteError(faceAmountControl, 'maxFaceAmount');
    return null;
  }

  wholeProductEligibilityValidatorInForce(control: AbstractControl, originalFaceAmount: number): ValidatorFn {
    return (control: FormGroup): ValidationErrors | null => {
      const faceAmountControl = control.get('faceAmount');
      const productControl = control.get('product');
      return this._wholeProductEligibilityValidatorInForce(faceAmountControl, productControl, originalFaceAmount);
    };
  }

  private _wholeProductEligibilityValidatorInForce(
    faceAmountControl: AbstractControl,
    productControl: AbstractControl,
    originalFaceAmount: number
  ): ValidationErrors | null {
    const faceAmount = faceAmountControl.value;
    const product = productControl.value;
    const isProductEligibile = true;
    if (faceAmount && product) {
      if (productEligibileForFaceIncreaseIllustration.indexOf(product) === -1 && faceAmount > originalFaceAmount) {
        this.addError(faceAmountControl, { productEligibility: { isProductEligibile } });
        return null;
      }
    }
    this.deleteError(faceAmountControl, 'productEligibility');
    return null;
  }

  minfaceIncreaseInterval(contract: AbstractControl, originalFaceAmount: number): ValidatorFn {
    return (control: FormGroup): ValidationErrors | null => {
      const faceAmountControl = control.get('faceAmount');
      return this._minfaceIncreaseInterval(faceAmountControl, originalFaceAmount);
    };
  }

  private _minfaceIncreaseInterval(
    faceAmountControl: AbstractControl,
    originalFaceAmount: number
  ): ValidationErrors | null {
    const faceAmount = faceAmountControl.value;
    const min = true;
    const faceIncreaseDiff = faceAmount - originalFaceAmount;
    if (faceAmount) {
      if (faceIncreaseDiff > 0 && faceIncreaseDiff < MinimumFaceIncreaseInterval) {
        this.addError(faceAmountControl, { minInterval: { min } });
        return null;
      }
    }
    this.deleteError(faceAmountControl, 'minInterval');
    return null;
  }

  jointAppWholeMaxFaceAmountValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const {
      clientInformation,
      jointAppQuote: { wholeLifeQuote },
    } = control.getRawValue();
    const faceAmountControl = control.get('jointAppQuote.wholeLifeQuote.faceAmount');
    const maxFaceAmountControl = control.get('jointAppQuote.wholeLifeQuote.maxFaceAmount');
    return this._wholeMaxFaceAmountValidator(
      clientInformation,
      wholeLifeQuote,
      faceAmountControl,
      maxFaceAmountControl,
      true
    );
  };

  private _termMinFaceAmountValidator(
    clientInformation: ClientInformation,
    termQuote: TermQuote,
    faceAmountControl: AbstractControl,
    isJoint = false
  ): ValidationErrors | null {
    const productType = ProductType.TERM;
    const faceAmount = faceAmountControl.value;
    const min = ValidationService.isMinimumFaceAmountMet({
      isJoint,
      healthClass: clientInformation.healthClass,
      productType,
      faceAmount,
      dob: clientInformation.dob,
    });

    if (min === true) {
      // @TODO HL-20489
      this.deleteError(faceAmountControl, 'minFaceAmount');
    } else {
      this.addError(faceAmountControl, { minFaceAmount: { min } });
    }
    return null;
  }

  termMinFaceAmountValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { clientInformation, termQuote } = control.value;
    const faceAmountControl = control.get('termQuote.faceAmount');
    return this._termMinFaceAmountValidator(clientInformation, termQuote, faceAmountControl);
  };

  jointAppTermMinFaceAmountValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const {
      clientInformation,
      jointAppQuote: { termQuote },
    } = control.value;
    const faceAmountControl = control.get('jointAppQuote.termQuote.faceAmount');
    return this._termMinFaceAmountValidator(clientInformation, termQuote, faceAmountControl, true);
  };

  private _termMaxFaceAmountValidator(
    clientInformation: ClientInformation,
    termQuote: TermQuote,
    faceAmountControl: AbstractControl,
    maxFaceAmountControl: AbstractControl
  ): ValidationErrors | null {
    const faceAmount = faceAmountControl.value;
    const maxFaceAmount = maxFaceAmountControl && maxFaceAmountControl.value ? maxFaceAmountControl.value : null;
    const isUsCitizen: boolean = ValidatorsService.isUSCitizen(clientInformation.citizenship);
    const max = this.validationService.isMaximumFaceAmountMetForTerm({
      dob: clientInformation.dob,
      faceAmount,
      maxFaceAmount,
      isUsCitizen,
    });

    if (max === true) {
      this.deleteError(faceAmountControl, 'maxFaceAmount');
    } else {
      this.addError(faceAmountControl, { maxFaceAmount: { max } });
    }

    return null;
  }

  termMaxFaceAmountValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { clientInformation, termQuote } = control.value;
    const faceAmountControl = control.get('termQuote.faceAmount');
    const maxFaceAmountControl = control.get('termQuote.maxFaceAmount');
    return this._termMaxFaceAmountValidator(clientInformation, termQuote, faceAmountControl, maxFaceAmountControl);
  };

  // this case is only used for InForce to determine that the faceamount is only a decrease of the current
  termMaxFaceAmountValidatorInForce(control: AbstractControl): ValidatorFn {
    return (control: FormGroup): ValidationErrors | null => {
      const faceAmountControl = control.get('faceAmount');
      const maxFaceAmountControl = control.get('maxFaceAmount');
      return this._termMaxFaceAmountValidatorInForce(faceAmountControl, maxFaceAmountControl);
    };
  }

  private _termMaxFaceAmountValidatorInForce(
    faceAmountControl: AbstractControl,
    maxFaceAmountControl: AbstractControl
  ): ValidationErrors | null {
    const faceAmount = faceAmountControl.value;
    const maxFaceAmount = maxFaceAmountControl && maxFaceAmountControl.value ? maxFaceAmountControl.value : null;
    let max;
    // if maxFaceAmount is greater than or equal to current face amount there is no error
    if (maxFaceAmount && maxFaceAmount >= faceAmount) {
      max = true;
    } else {
      max = maxFaceAmount;
    }

    if (max === true) {
      this.deleteError(faceAmountControl, 'maxFaceAmount');
    } else {
      this.addError(faceAmountControl, { maxFaceAmount: { max } });
    }

    return null;
  }

  jointAppTermMaxFaceAmountValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const {
      clientInformation,
      jointAppQuote: { termQuote },
    } = control.value;
    const faceAmountControl = control.get('jointAppQuote.termQuote.faceAmount');
    const maxFaceAmountControl = control.get('jointAppQuote.termQuote.maxFaceAmount');
    return this._termMaxFaceAmountValidator(clientInformation, termQuote, faceAmountControl, maxFaceAmountControl);
  };

  agentLicenseValidator: AsyncValidatorFn = (group: FormGroup): Observable<ValidationErrors | null> => {
    const {
      productType,
      clientInformation: { contractState },
    }: QuoteForm = group.getRawValue();
    // agent license check is not needed when wholesaler is logged in
    if (this.controller.hasWholesaleProxyLoggedIn()) {
      return of(null);
    }

    if (productType === Product.productTypes.Term.code) {
      return of(null);
    }

    if (StateSpecification.LicenseRequiredStates.indexOf(contractState) < 0) {
      return of(null);
    }

    return this.agentLicenseService.getStateLicense().pipe(
      take(1),
      map((licenses) =>
        licenses.find((license) => license.state === contractState)
          ? null
          : {
              invalidLicense: `We don’t have your ${Assumptions.stateValues[contractState]} license number on file. Please contact our support team.`,
            }
      )
    );
  };

  agentPhoneNumberValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const {
      productType,
      clientInformation: { contractState },
    }: QuoteForm = control.value;

    if (productType === Product.productTypes.Term.code) {
      return null;
    }

    const agent: Agent = this.controller.getAgent();
    if (agent && StateSpecification.PhoneNumberRequiredStates.indexOf(contractState) > -1 && !agent.phone.number) {
      return {
        invalidLicense: 'We don’t have your phone number on file. Please contact our support team.',
      };
    }

    return null;
  };

  private _termLengthValidator(
    clientInformation: ClientInformation,
    termQuote: TermQuote,
    coverageTermControl: AbstractControl,
    faceAmountControl: AbstractControl
  ): ValidationErrors | null {
    const healthClass = clientInformation.healthClass;
    const isTobaccoClass = healthClass ? healthClass.indexOf('TOBACCO') > -1 : false;

    const dob: Date = clientInformation.dob;
    const coverageTerm = termQuote.coverageTerm;
    const submittedAt = termQuote.submittedAt;

    this.deleteError(coverageTermControl, 'coverageTerm');

    const ecp = termQuote.ecp;
    const faceAmount = faceAmountControl.value;

    const tobaccoClassKey = isTobaccoClass ? 'TOBACCO' : 'NON_TOBACCO';
    const prodKey = coverageTerm + (ecp ? 'ECP' : '');
    const amountKey = faceAmount <= 1e6 ? FaceAmountLimit.UPTO_1M : FaceAmountLimit.UPTO_3M;

    // ECP is not supported for Term 10 or ART
    if (faceAmount && coverageTerm && !(ecp && coverageTerm <= 10)) {
      const ageRange = CPConvertibleTermAgeRanges[prodKey][amountKey][tobaccoClassKey];
      const minAge = ageRange.minAge;
      const maxAge = ageRange.maxAge;
      if (
        CommonUtils.isYoungerThanAge(dob, minAge, submittedAt) ||
        CommonUtils.isOlderThanOrEqualToAge(dob, maxAge, submittedAt)
      ) {
        this.addError(coverageTermControl, { coverageTerm: true });
      }
    }
    return null;
  }

  termLengthValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { clientInformation, termQuote } = control.value;
    const coverageTermControl = control.get('termQuote.coverageTerm');
    const faceAmountControl = control.get('termQuote.faceAmount');
    return this._termLengthValidator(clientInformation, termQuote, coverageTermControl, faceAmountControl);
  };

  jointAppTermLengthValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const {
      clientInformation,
      jointAppQuote: { termQuote },
    } = control.value;
    const coverageTermControl = control.get('jointAppQuote.termQuote.coverageTerm');
    const faceAmountControl = control.get('jointAppQuote.termQuote.faceAmount');
    return this._termLengthValidator(clientInformation, termQuote, coverageTermControl, faceAmountControl);
  };

  private _waiverOfPremiumValidator(
    clientInformation: ClientInformation,
    waiverOfPremiumControl: AbstractControl
  ): ValidationErrors | null {
    const dob: Date = clientInformation.dob;
    const healthClass = clientInformation.healthClass;
    let invalidHealthClass = false;
    if (RateClasses.isTableRatedByStringCode(healthClass)) {
      const rateClass = RateClasses.findByStringCode(healthClass);
      if (
        Number(rateClass.code) < Number(RateClasses.TABLE_B.code) ||
        Number(rateClass.code) >
          (rateClass.smoker === true ? Number(RateClasses.TABLE_F_TOBACCO.code) : Number(RateClasses.TABLE_F.code))
      ) {
        invalidHealthClass = true;
      }
    }

    if (CommonUtils.isYoungerThanAge(dob, RiderConstants.wop.maxAge) && !invalidHealthClass) {
      if (waiverOfPremiumControl.disabled) {
        waiverOfPremiumControl.enable();
      }
    } else {
      if (waiverOfPremiumControl.enabled) {
        waiverOfPremiumControl.disable();
        waiverOfPremiumControl.patchValue(false);
      }
    }

    return null;
  }

  wholeWaiverOfPremiumValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { clientInformation } = control.value;
    const waiverOfPremiumControl = control.get('wholeLifeQuote.waiverOfPremium');
    return this._waiverOfPremiumValidator(clientInformation, waiverOfPremiumControl);
  };

  jointWholeWaiverOfPremiumValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { clientInformation } = control.value;
    const waiverOfPremiumControl = control.get('jointAppQuote.wholeLifeQuote.waiverOfPremium');
    return this._waiverOfPremiumValidator(clientInformation, waiverOfPremiumControl);
  };

  jointTermWaiverOfPremiumValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { clientInformation } = control.value;
    const waiverOfPremiumControl = control.get('jointAppQuote.termQuote.waiverOfPremium');
    return this._waiverOfPremiumValidator(clientInformation, waiverOfPremiumControl);
  };

  termWaiverOfPremiumValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { clientInformation } = control.value;
    const waiverOfPremiumControl = control.get('termQuote.waiverOfPremium');
    return this._waiverOfPremiumValidator(clientInformation, waiverOfPremiumControl);
  };

  maxAgeArtValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { clientInformation, termQuote } = control.value;
    const coverageTermControl = control.get('termQuote.coverageTerm');
    return this._maxAgeArtValidator(clientInformation, termQuote, coverageTermControl);
  };

  jointAppMaxAgeArtValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const {
      clientInformation,
      jointAppQuote: { termQuote },
    } = control.value;
    const coverageTermControl = control.get('jointAppQuote.termQuote.coverageTerm');
    return this._maxAgeArtValidator(clientInformation, termQuote, coverageTermControl);
  };

  private _maxAgeArtValidator(
    clientInformation: ClientInformation,
    termQuote: TermQuote,
    coverageTermControl: AbstractControl
  ): ValidationErrors | null {
    const min = Product.productSpecs.CASTerm.minAge;
    const breakdown = Product.productSpecs.CASTerm.termBreakdown['1'];
    const max = breakdown.maxAge;
    const dob: Date = clientInformation.dob;
    if (termQuote.coverageTerm === 1 && CommonUtils.isOlderThanOrEqualToAge(dob, max)) {
      this.addError(coverageTermControl, { dob: { min, max } });
    } else {
      this.deleteError(coverageTermControl, 'dob');
    }
    return null;
  }

  private _ecpValidator(
    clientInformation: ClientInformation,
    termQuote: TermQuote,
    ecpControl: AbstractControl
  ): ValidationErrors | null {
    const dob: Date = clientInformation.dob;

    const coverageTerm = termQuote.coverageTerm;
    if (
      coverageTerm <= 10 ||
      (CommonUtils.isSmokerHealthClass(clientInformation.healthClass) && coverageTerm === 30
        ? CommonUtils.isOlderThanOrEqualToAge(
            dob,
            Product.products.CASTermECP.productSpecs.termBreakdown[coverageTerm].tobaccoMaxAge
          )
        : CommonUtils.isOlderThanOrEqualToAge(
            dob,
            Product.products.CASTermECP.productSpecs.termBreakdown[coverageTerm].maxAge
          ))
    ) {
      if (ecpControl.enabled) {
        ecpControl.disable();
        ecpControl.patchValue(false);
      }
    } else {
      if (ecpControl.disabled) {
        ecpControl.enable();
      }
    }
    return null;
  }

  ecpValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { clientInformation, termQuote } = control.value;
    const ecpControl = control.get('termQuote.ecp');
    return this._ecpValidator(clientInformation, termQuote, ecpControl);
  };

  jointAppEcpValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const {
      clientInformation,
      jointAppQuote: { termQuote },
    } = control.value;
    const ecpControl = control.get('jointAppQuote.termQuote.ecp');
    return this._ecpValidator(clientInformation, termQuote, ecpControl);
  };

  private _taxBracketValidator(
    wholeLifeQuote: WholeLifeQuote,
    taxBracketControl: AbstractControl
  ): ValidationErrors | null {
    const dividendOption = wholeLifeQuote.dividendOption;
    const taxBracket = wholeLifeQuote.taxBracket;
    if (['Cash', 'PUA', 'Accumulate'].includes(dividendOption) && !taxBracket) {
      this.addError(taxBracketControl, { required: true });
    } else {
      this.deleteError(taxBracketControl, 'required');
    }
    return null;
  }

  taxBracketValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { wholeLifeQuote } = control.value;
    const taxBracketControl = control.get('wholeLifeQuote.taxBracket');
    return this._taxBracketValidator(wholeLifeQuote, taxBracketControl);
  };

  jointAppTaxBracketValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const {
      jointAppQuote: { wholeLifeQuote },
    } = control.value;
    const taxBracketControl = control.get('jointAppQuote.wholeLifeQuote.taxBracket');
    return this._taxBracketValidator(wholeLifeQuote, taxBracketControl);
  };

  private _jointAppFaceAmountValidator(
    termFaceAmountControl: AbstractControl,
    wholeFaceAmountControl: AbstractControl,
    rtrAmountControl: AbstractControl,
    clientInformation: ClientInformation,
    maxFaceAmountControl?: AbstractControl,
    termPolicyType?: string,
    wholePolicyType?: string
  ): ValidationErrors | null {
    const termFaceAmount =
      termPolicyType === POLICY_TYPES.AMEND_REMOVE_SECONDARY
        ? 0
        : termFaceAmountControl
        ? termFaceAmountControl.value
        : 0;
    const wholeFaceAmount = wholePolicyType === POLICY_TYPES.AMEND_REMOVE_PRIMARY ? 0 : wholeFaceAmountControl.value;
    const rtrEnabled = rtrAmountControl && rtrAmountControl.parent.get('enabled').value;
    const rtrAmount =
      wholePolicyType === POLICY_TYPES.AMEND_REMOVE_PRIMARY ? 0 : rtrEnabled ? rtrAmountControl.value : 0;
    const maxFaceAmount = maxFaceAmountControl ? maxFaceAmountControl.value : 0;
    const isUsCitizen: boolean = ValidatorsService.isUSCitizen(clientInformation.citizenship);
    const dob: Date = clientInformation.dob;
    const maxTotalFaceAmountAllowed = maxFaceAmount
      ? maxFaceAmount
      : ValidationStatic.getMaximumAllowedForTerm({ dob, isUsCitizen });

    if (termFaceAmount + wholeFaceAmount + rtrAmount > maxTotalFaceAmountAllowed) {
      if (termFaceAmountControl) {
        this.addError(termFaceAmountControl, { combinedMax: { max: maxTotalFaceAmountAllowed } });
      }
      this.addError(wholeFaceAmountControl, { combinedMax: { max: maxTotalFaceAmountAllowed } });
      if (rtrAmountControl && rtrEnabled) {
        this.addError(rtrAmountControl, { combinedMax: { max: maxTotalFaceAmountAllowed } });
      }
    } else {
      if (termFaceAmountControl) {
        this.deleteError(termFaceAmountControl, 'combinedMax');
      }
      this.deleteError(wholeFaceAmountControl, 'combinedMax');
      if (rtrAmountControl) {
        this.deleteError(rtrAmountControl, 'combinedMax');
      }
    }
    return null;
  }

  jointAppFaceAmountValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { clientInformation }: QuoteForm = control.value;
    const dob: Date = clientInformation.dob;
    const termFaceAmountControl = control.get('jointAppQuote.termQuote.faceAmount');
    const wholeFaceAmountControl = control.get('jointAppQuote.wholeLifeQuote.faceAmount');
    const maxFaceAmountControl = control.get('jointAppQuote.wholeLifeQuote.maxFaceAmount');
    const rtrAmountControl = control.get('jointAppQuote.wholeLifeQuote.riders.rtr.amount');
    const termPolicyType = control.get('jointAppQuote.termQuote.type').value;
    const wholePolicyType = control.get('jointAppQuote.wholeLifeQuote.type').value;

    if (termFaceAmountControl && wholeFaceAmountControl && dob) {
      return this._jointAppFaceAmountValidator(
        termFaceAmountControl,
        wholeFaceAmountControl,
        rtrAmountControl,
        clientInformation,
        maxFaceAmountControl,
        termPolicyType,
        wholePolicyType
      );
    }
    return null;
  };

  emailFormatValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control?.value || Utils.isEmailString(control.value)) {
      return null;
    } else {
      return { email: true };
    }
  };

  ownerEmailValidator: AsyncValidatorFn = (control: AbstractControl): Observable<ValidationErrors | null> => {
    // we should remove the call of this function in form-builder.service.ts when we remove the enableMultipleAdvisorsOneCustomer FeatureFlag
    // proxyingAsAgent is null when wholesaler acts as themself
    // email validation is not needed for wholesalers
    if (!this.controller.getAgent()) {
      return of(null);
    }

    if (this.controller.enableMultipleAdvisorsOneCustomer) {
      return of(null);
    } else {
      const enteredEmail = control.value;
      // add some "cancelable" delay so that each key stroke doesn't fire an async validation to server
      return timer(500).pipe(
        switchMap(() => this.controller.customerAccountExistsForEmail(enteredEmail)),
        map((value) => {
          // customerId is only populated from the search
          const customerId = control.parent.get('customerId').value || '';
          if (!value || customerId.length > 0) {
            return null;
          } else {
            return { emailHasAccountDoSearch: true };
          }
        })
      );
    }
  };

  conversionOwnerEmailValidator: AsyncValidatorFn = (control: AbstractControl): Observable<ValidationErrors | null> => {
    const enteredEmail = control.value;
    // add some "cancelable" delay so that each key stroke doesn't fire an async validation to server
    if (
      CoverpathUtils.isSanderlingPolicy(Utils.get(this, 'controller.customer.application.primaryPolicy.product')) &&
      !Utils.get(this, 'controller.customer.email')
    ) {
      return timer(500).pipe(
        switchMap(() => this.controller.customerAccountExistsForEmail(enteredEmail)),
        map((value) => {
          if (!value) {
            return null;
          } else {
            return { emailHasAccount: true };
          }
        })
      );
    } else {
      return of(null);
    }
  };

  addError(control: AbstractControl, error: ValidationErrors) {
    if (control.errors) {
      control.setErrors({ ...control.errors, ...error });
    } else {
      control.setErrors(error);
    }
  }

  private deleteError(control: AbstractControl, key: string) {
    if (control.errors && control.errors[key]) {
      const errors = { ...control.errors };
      delete errors[key];
      if (Object.keys(errors).length > 0) {
        control.setErrors(errors);
      } else {
        control.setErrors(null);
      }
    }
  }

  private getInsuranceAge(dob: Date): number {
    if (dob) {
      const asOfDate = Utils.now();
      const insDate = Utils.getCurrentMidYearDate(dob, asOfDate);
      return Utils.yearsBetween(dob, asOfDate) + (insDate <= asOfDate ? 1 : 0);
    } else {
      return 0;
    }
  }

  wholeGirValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const girAmountControl = control.get('wholeLifeQuote.riders.gir.amount');
    const { clientInformation, wholeLifeQuote } = control.getRawValue() as QuoteForm;
    return this._girValidator(clientInformation, wholeLifeQuote, girAmountControl);
  };

  /** Returns a faceAmount validator for post-issue GIR illustrations. Not a global validator. */
  createGirExecutionMaxFaceValidator(girAmount: number): ValidatorFn {
    return (faceAmountControl: FormControl): ValidationErrors | null => {
      if (girAmount && faceAmountControl.value > girAmount) {
        return { girExecutionMaxFace: { girAmount } };
      }
      return null;
    };
  }

  jointGirValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const girAmountControl = control.get('jointAppQuote.wholeLifeQuote.riders.gir.amount');
    const {
      clientInformation,
      jointAppQuote: { wholeLifeQuote },
    } = control.getRawValue() as QuoteForm;
    return this._girValidator(clientInformation, wholeLifeQuote, girAmountControl);
  };

  private _girValidator(
    clientInformation: ClientInformation,
    wholeLifeQuote: WholeLifeQuote,
    girAmountControl: AbstractControl
  ): ValidationErrors | null {
    this.deleteError(girAmountControl, 'gir');
    const gir = wholeLifeQuote.riders.gir;
    const dob: Date = clientInformation.dob;
    const faceAmount = wholeLifeQuote.faceAmount;

    if (!gir || !gir.enabled) {
      return null;
    }

    const result = GIRValidationUtils.validateGirRider(dob, faceAmount, gir.amount, clientInformation.healthClass);
    if (result) {
      this.addError(girAmountControl, result);
    }
  }

  wholeRtrValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const rtrAmountControl = control.get('wholeLifeQuote.riders.rtr.amount');
    const wholeLifeFaceAmountControl = control.get('wholeLifeQuote.faceAmount');
    const maxFaceAmountControl = control.get('wholeLifeQuote.maxFaceAmount');
    const product = control.get('wholeLifeQuote.product').value;
    const { clientInformation, wholeLifeQuote } = control.getRawValue() as QuoteForm;
    return this._rtrValidator(
      wholeLifeQuote,
      rtrAmountControl,
      clientInformation,
      product,
      null,
      null,
      wholeLifeFaceAmountControl,
      maxFaceAmountControl
    );
  };

  jointRtrValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const rtrAmountControl = control.get('jointAppQuote.wholeLifeQuote.riders.rtr.amount');
    const termFaceAmountControl = control.get('jointAppQuote.termQuote.faceAmount');
    const wholeLifeFaceAmountControl = control.get('jointAppQuote.wholeLifeQuote.faceAmount');
    const maxFaceAmountControl = control.get('jointAppQuote.wholeLifeQuote.maxFaceAmount');
    const product = control.get('jointAppQuote.wholeLifeQuote.product').value;
    const {
      clientInformation,
      jointAppQuote: { wholeLifeQuote, termQuote },
    } = control.getRawValue() as QuoteForm;
    return this._rtrValidator(
      wholeLifeQuote,
      rtrAmountControl,
      clientInformation,
      product,
      termQuote,
      termFaceAmountControl,
      wholeLifeFaceAmountControl,
      maxFaceAmountControl
    );
  };

  private _rtrValidator(
    wholeLifeQuote: WholeLifeQuote,
    rtrAmountControl: AbstractControl,
    clientInformation: ClientInformation,
    product: string,
    termQuote?: TermQuote,
    termFaceAmountControl?: AbstractControl,
    wholeLifeFaceAmountControl?: AbstractControl,
    maxFaceAmountControl?: AbstractControl
  ): ValidationErrors | null {
    const termFaceAmount =
      termQuote && termQuote.type === POLICY_TYPES.AMEND_REMOVE_SECONDARY
        ? 0
        : termFaceAmountControl
        ? termFaceAmountControl.value
        : 0;
    const wholeLifeFaceAmount =
      wholeLifeQuote.type === POLICY_TYPES.AMEND_REMOVE_PRIMARY
        ? 0
        : wholeLifeFaceAmountControl
        ? wholeLifeFaceAmountControl.value
        : 0;
    const faceAmount = termFaceAmount + wholeLifeFaceAmount;
    const maxFaceAmount = wholeLifeQuote.maxFaceAmount;
    const rtr = wholeLifeQuote.riders.rtr;
    const rtrEnabled: boolean = rtr && rtr.enabled;
    const alir = wholeLifeQuote.riders.alir;
    const alirEnabled: boolean = alir && alir.enabled;
    const dob: Date = clientInformation.dob;
    const isUsCitizen: boolean = ValidatorsService.isUSCitizen(clientInformation.citizenship);
    this.deleteError(rtrAmountControl, 'combinedMax');
    this.deleteError(rtrAmountControl, 'rtr');
    if (termFaceAmountControl) {
      this.deleteError(termFaceAmountControl, 'combinedMax');
    }
    this.deleteError(wholeLifeFaceAmountControl, 'combinedMax');
    if (termFaceAmountControl && wholeLifeFaceAmountControl && dob) {
      this._jointAppFaceAmountValidator(
        termFaceAmountControl,
        wholeLifeFaceAmountControl,
        rtrEnabled ? rtrAmountControl : null,
        clientInformation,
        maxFaceAmountControl,
        termQuote ? termQuote.type : null,
        wholeLifeQuote.type
      );
    }
    if (!rtr || !rtr.enabled) {
      return null;
    }

    const scheduledAlirAmount = alirEnabled ? alir.amount : 0;
    if (rtrEnabled && rtrAmountControl.valid && wholeLifeQuote.type !== POLICY_TYPES.AMEND_REMOVE_PRIMARY) {
      const result = RTRValidationUtils.validateRtrRider(
        dob,
        faceAmount,
        rtr.amount,
        product,
        isUsCitizen,
        scheduledAlirAmount,
        maxFaceAmount
      );
      if (result) {
        const combinedMaxValue = result.rtr.combinedMax;
        if (combinedMaxValue) {
          if (termFaceAmountControl) {
            this.addError(termFaceAmountControl, { combinedMax: { max: combinedMaxValue } });
          }
          this.addError(wholeLifeFaceAmountControl, { combinedMax: { max: combinedMaxValue } });
          this.addError(rtrAmountControl, { combinedMax: { max: combinedMaxValue } });
        } else {
          this.addError(rtrAmountControl, result);
        }
      }
    }
  }

  jointCirValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const {
      clientInformation,
      jointAppQuote: { wholeLifeQuote },
    } = control.getRawValue() as QuoteForm;
    const cirControl = control.get('jointAppQuote.wholeLifeQuote.riders.cir');
    const wholeLifeGlobalErrorControl = control.get('jointAppQuote.wholeLifeQuote.globalError');
    const baseBenefitPoolControl = control.get('jointAppQuote.wholeLifeQuote.riders.cir.amount');
    const predictedBenefitAgeControl = control.get('jointAppQuote.wholeLifeQuote.riders.cir.predictedBenefitAge');
    return this._cirValidator(
      clientInformation,
      wholeLifeQuote,
      cirControl,
      baseBenefitPoolControl,
      wholeLifeGlobalErrorControl,
      predictedBenefitAgeControl
    );
  };

  wholeCirValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const cirControl = control.get('wholeLifeQuote.riders.cir');
    const { clientInformation, wholeLifeQuote } = control.getRawValue() as QuoteForm;
    const wholeLifeGlobalErrorControl = control.get('wholeLifeQuote.globalError');
    const baseBenefitPoolControl = control.get('wholeLifeQuote.riders.cir.amount');
    const predictedBenefitAgeControl = control.get('wholeLifeQuote.riders.cir.predictedBenefitAge');
    return this._cirValidator(
      clientInformation,
      wholeLifeQuote,
      cirControl,
      baseBenefitPoolControl,
      wholeLifeGlobalErrorControl,
      predictedBenefitAgeControl
    );
  };

  private _cirValidator(
    clientInformation: ClientInformation,
    wholeLifeQuote: WholeLifeQuote,
    cirControl: AbstractControl,
    baseBenefitPoolControl: AbstractControl,
    wholeLifeGlobalErrorControl: AbstractControl,
    predictedBenefitAgeControl: AbstractControl
  ): ValidationErrors | null {
    this.deleteError(baseBenefitPoolControl, 'cir');
    this.deleteError(wholeLifeGlobalErrorControl, 'cir');
    this.deleteError(predictedBenefitAgeControl, 'cir');

    if (!cirControl || !cirControl.enabled || !cirControl.get('enabled').value) {
      return null;
    }

    if (cirControl.get('enabled').value && baseBenefitPoolControl.value !== null) {
      const healthClass = clientInformation.healthClass;
      const dob: Date = clientInformation.dob;
      const faceAmount = wholeLifeQuote.faceAmount;
      const state = clientInformation.contractState;

      const result = CIRValidationUtils.validateCirRider(
        dob,
        faceAmount,
        baseBenefitPoolControl.value,
        healthClass,
        predictedBenefitAgeControl.value,
        state
      );

      if (result) {
        const ageResult = result.age;
        const baseBenefitPoolResult = result.baseBenefitPool;
        const healthClassResult = result.healthClass;
        const predictedBenefitAgeResult = result.predictedBenefitAge;
        const faceAmountResult = result.faceAmount;
        const stateResult = result.state;

        if (!ageResult.isValid) {
          this.addError(baseBenefitPoolControl, { cir: ageResult });
          this.addError(wholeLifeGlobalErrorControl, { cir: ageResult });
        }
        if (baseBenefitPoolControl.value !== null && !baseBenefitPoolResult.isValid && baseBenefitPoolResult.message) {
          if (baseBenefitPoolResult.isGlobalError) {
            this.addError(wholeLifeGlobalErrorControl, { cir: baseBenefitPoolResult });
          }
          this.addError(baseBenefitPoolControl, { cir: baseBenefitPoolResult });
        }

        if (!healthClassResult.isValid) {
          this.addError(baseBenefitPoolControl, { cir: healthClassResult });
          this.addError(wholeLifeGlobalErrorControl, { cir: healthClassResult });
        }

        if (!stateResult.isValid) {
          this.addError(wholeLifeGlobalErrorControl, { cir: stateResult });
        }

        if (!predictedBenefitAgeResult.isValid) {
          this.addError(predictedBenefitAgeControl, { cir: predictedBenefitAgeResult });
        }

        if (!faceAmountResult.isValid) {
          this.addError(baseBenefitPoolControl, { cir: faceAmountResult });
          this.addError(wholeLifeGlobalErrorControl, { cir: faceAmountResult });
        }
      }
    }
  }

  wholeAlirValidator: ValidatorFn = (control: FormGroup) => {
    const alirControl = control.get('wholeLifeQuote.riders.alir');
    const wholeLifeFaceAmount = control.get('wholeLifeQuote.faceAmount').value;

    let wholeLifeAnnualBasePremium = control.get('wholeLifeQuote.annualPremium').value;
    wholeLifeAnnualBasePremium = wholeLifeAnnualBasePremium ? wholeLifeAnnualBasePremium.valueOf() : 0;

    const wholeLifeGlobalErrorControl = control.get('wholeLifeQuote.globalError');
    const { clientInformation, wholeLifeQuote } = control.getRawValue() as QuoteForm;

    const maxFaceAmountControl = control.get('wholeLifeQuote.maxFaceAmount');
    const maxFaceAllowed = maxFaceAmountControl && maxFaceAmountControl.value ? maxFaceAmountControl.value : null;

    return this._alirValidator(
      alirControl,
      wholeLifeFaceAmount,
      wholeLifeAnnualBasePremium,
      clientInformation,
      wholeLifeQuote,
      wholeLifeGlobalErrorControl,
      maxFaceAllowed
    );
  };

  jointAlirValidator: ValidatorFn = (control: FormGroup) => {
    const alirControl = control.get('jointAppQuote.wholeLifeQuote.riders.alir');
    const termFaceAmount = control.get('jointAppQuote.termQuote.faceAmount').value;
    const wholeLifeFaceAmount = control.get('jointAppQuote.wholeLifeQuote.faceAmount').value;
    const jointAppFaceAmount = termFaceAmount + wholeLifeFaceAmount;

    let wholeLifeAnnualBasePremium = control.get('jointAppQuote.wholeLifeQuote.annualPremium').value;
    wholeLifeAnnualBasePremium = wholeLifeAnnualBasePremium ? wholeLifeAnnualBasePremium.valueOf() : 0;

    const wholeLifeGlobalErrorControl = control.get('jointAppQuote.wholeLifeQuote.globalError');
    const { clientInformation, jointAppQuote } = control.getRawValue() as QuoteForm;
    const wholeLifeQuote = jointAppQuote.wholeLifeQuote;

    const maxFaceAmountControl = control.get('jointAppQuote.wholeLifeQuote.maxFaceAmount');
    const maxFaceAllowed = maxFaceAmountControl && maxFaceAmountControl.value ? maxFaceAmountControl.value : null;

    return this._alirValidator(
      alirControl,
      jointAppFaceAmount,
      wholeLifeAnnualBasePremium,
      clientInformation,
      wholeLifeQuote,
      wholeLifeGlobalErrorControl,
      maxFaceAllowed
    );
  };

  private _getPremiumAndValidateAlirAmount(
    fieldControl: AbstractControl,
    wholeLifeQuote: WholeLifeQuote,
    annualBasePremium: number,
    quoteFaceAmount: number,
    wholeLifeGlobalErrorControl: AbstractControl,
    clientInformation: ClientInformation,
    validateAmountFn,
    maxFaceAllowed?: number
  ) {
    if (fieldControl.value && annualBasePremium <= 0) {
      const validationReq = {
        wholeLifeQuote,
        fieldControl,
        quoteFaceAmount,
        wholeLifeGlobalErrorControl,
        clientInformation,
        maxFaceAllowed,
        validateAmountFn,
      };
      this.debouncedFetchPremium(validationReq);
    } else {
      validateAmountFn(
        annualBasePremium,
        wholeLifeQuote,
        fieldControl,
        quoteFaceAmount,
        wholeLifeGlobalErrorControl,
        clientInformation,
        maxFaceAllowed
      );
    }
  }

  private _alirValidator(
    alirControl: AbstractControl,
    quoteFaceAmount: number,
    wholeLifeAnnualBasePremium: number,
    clientInformation: ClientInformation,
    wholeLifeQuote: WholeLifeQuote,
    wholeLifeGlobalErrorControl: AbstractControl,
    maxFaceAllowed: number
  ) {
    const alir = wholeLifeQuote.riders.alir;
    if (wholeLifeQuote.exchange1035.enabled && alir.enabled) {
      this.addError(wholeLifeGlobalErrorControl, {
        alir: {
          isValid: false,
          message: `ALIR error: a policy cannot have both Scheduled ALIR and a 1035 exchange.`,
          isGlobalError: true,
        },
      });
      this.addError(alirControl.get('amount'), { alir: { message: '' } });
      return null;
    }

    if (!clientInformation.dob || !alir || !alir.enabled || quoteFaceAmount <= 0 || !wholeLifeQuote.product) {
      this.deleteError(alirControl.get('amount'), 'alir');
      this.deleteError(alirControl.get('plannedYears'), 'alir');
      this.deleteError(wholeLifeGlobalErrorControl, 'alir');
      return null;
    }

    this._getPremiumAndValidateAlirAmount(
      alirControl.get('amount'),
      wholeLifeQuote,
      wholeLifeAnnualBasePremium,
      quoteFaceAmount,
      wholeLifeGlobalErrorControl,
      clientInformation,
      this.validateAlirAmount.bind(this),
      maxFaceAllowed
    );

    const validation = ValidationService.isValidAlirPlannedYears(
      alirControl.get('plannedYears').value,
      Product.productSpecs[wholeLifeQuote.product].term,
      clientInformation.dob
    );
    if (validation.result) {
      this.deleteError(alirControl.get('plannedYears'), 'alir');
    } else {
      this.addError(alirControl.get('plannedYears'), { alir: { message: validation.error } });
    }

    return null;
  }

  static isValidAlirAmount(
    annualPremium: number,
    rtrAmount: number,
    product: string,
    scheduledAlirAmount: number,
    faceAmount: number,
    unscheduledAlirAmount: number,
    dob: Date,
    isCitizen: boolean,
    paymentFrequency: string,
    maxFaceAllowed?: number
  ) {
    const insuranceAge = ValidationService.getInsuranceAge(dob);
    const actualAge = ValidationService.publicGetAge(dob);
    return WholeLifeValidationUtils.isValidAlirAmount(
      scheduledAlirAmount,
      unscheduledAlirAmount,
      faceAmount,
      rtrAmount,
      insuranceAge,
      actualAge,
      isCitizen,
      annualPremium,
      product,
      paymentFrequency,
      maxFaceAllowed
    );
  }

  private validateAlirAmount(
    annualBasePremium: number,
    wholeLifeQuote: WholeLifeQuote,
    alirAmountControl: AbstractControl,
    faceAmount: number,
    wholeLifeGlobalErrorControl: AbstractControl,
    clientInformation: ClientInformation,
    maxFaceAllowed?: number
  ) {
    const validationResult = ValidatorsService.isValidAlirAmount(
      annualBasePremium,
      wholeLifeQuote.riders.rtr.enabled ? wholeLifeQuote.riders.rtr.amount : 0,
      wholeLifeQuote.product,
      wholeLifeQuote.riders.alir.enabled ? alirAmountControl.value : null,
      faceAmount,
      null,
      clientInformation.dob,
      ValidatorsService.isUSCitizen(clientInformation.citizenship),
      CommonUtils.makePaymentFrequencySafeForPolicy(wholeLifeQuote.paymentFrequency),
      maxFaceAllowed
    );

    if (validationResult.isValid) {
      this.deleteError(alirAmountControl, 'alir');
      this.deleteError(wholeLifeGlobalErrorControl, 'alir');
      this.deleteError(wholeLifeGlobalErrorControl, 'details');
    } else if (!validationResult.isGlobalError) {
      this.addError(alirAmountControl, { alir: { message: validationResult.message } });
    } else {
      this.addError(wholeLifeGlobalErrorControl, {
        alir: {
          message: validationResult.message,
          details: validationResult.details ? validationResult.details : undefined,
        },
      });

      // this is needed so the value is not saved for ALIR amount
      this.addError(alirAmountControl, { alir: { message: '' } });
    }
  }

  wholeExchange1035Validator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { wholeLifeQuote, clientInformation } = control.value as QuoteForm;
    const cashValueTransferControl = control.get('wholeLifeQuote.exchange1035.cashValueTransfer');
    const wholeLifeGlobalErrorControl = control.get('wholeLifeQuote.globalError');

    const wholeLifeFaceAmount = control.get('wholeLifeQuote.faceAmount').value;

    let wholeLifeAnnualBasePremium = control.get('wholeLifeQuote.annualPremium').value;
    wholeLifeAnnualBasePremium = wholeLifeAnnualBasePremium ? wholeLifeAnnualBasePremium.valueOf() : 0;

    const maxFaceAmountControl = control.get('wholeLifeQuote.maxFaceAmount');
    const maxFaceAllowed = maxFaceAmountControl && maxFaceAmountControl.value ? maxFaceAmountControl.value : null;

    return this._exchange1035Validator(
      wholeLifeQuote,
      clientInformation,
      wholeLifeFaceAmount,
      cashValueTransferControl,
      wholeLifeGlobalErrorControl,
      maxFaceAllowed,
      wholeLifeAnnualBasePremium
    );
  };

  jointExchange1035Validator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const { jointAppQuote, clientInformation } = control.value as QuoteForm;
    const wholeLifeQuote = jointAppQuote.wholeLifeQuote;
    const cashValueTransferControl = control.get('jointAppQuote.wholeLifeQuote.exchange1035.cashValueTransfer');
    const wholeLifeGlobalErrorControl = control.get('jointAppQuote.wholeLifeQuote.globalError');

    const wholeLifeFaceAmount = control.get('jointAppQuote.wholeLifeQuote.faceAmount').value;

    let wholeLifeAnnualBasePremium = control.get('jointAppQuote.wholeLifeQuote.annualPremium').value;
    wholeLifeAnnualBasePremium = wholeLifeAnnualBasePremium ? wholeLifeAnnualBasePremium.valueOf() : 0;

    const termFaceAmount = control.get('jointAppQuote.termQuote.faceAmount').value;
    const jointFaceAmount = wholeLifeFaceAmount + termFaceAmount;

    const maxFaceAmountControl = control.get('jointAppQuote.wholeLifeQuote.maxFaceAmount');
    const maxFaceAllowed = maxFaceAmountControl && maxFaceAmountControl.value ? maxFaceAmountControl.value : null;

    return this._exchange1035Validator(
      wholeLifeQuote,
      clientInformation,
      jointFaceAmount,
      cashValueTransferControl,
      wholeLifeGlobalErrorControl,
      maxFaceAllowed,
      wholeLifeAnnualBasePremium
    );
  };

  private _exchange1035Validator(
    wholeLifeQuote: WholeLifeQuote,
    clientInformation: ClientInformation,
    quoteFaceAmount: number,
    cashValueTransferControl: AbstractControl,
    wholeLifeGlobalErrorControl: AbstractControl,
    maxFaceAllowed: number,
    wholeLifeAnnualBasePremium: number
  ): ValidationErrors | null {
    const { exchange1035 } = wholeLifeQuote;

    if (
      exchange1035 &&
      exchange1035.enabled &&
      wholeLifeQuote.riders &&
      wholeLifeQuote.riders.alir &&
      wholeLifeQuote.riders.alir.enabled
    ) {
      this.addError(wholeLifeGlobalErrorControl, {
        exchange1035: {
          isValid: false,
          message: `ALIR error: a policy cannot have both Scheduled ALIR and a 1035 exchange.`,
          isGlobalError: true,
        },
      });
      this.addError(cashValueTransferControl, { exchange1035: { message: '' } });
      return null;
    }

    this.deleteError(cashValueTransferControl, 'exchange1035');
    this.deleteError(wholeLifeGlobalErrorControl, 'exchange1035');

    if (!exchange1035 || !exchange1035.enabled || quoteFaceAmount <= 0 || !wholeLifeQuote.product) {
      return null;
    }

    this._getPremiumAndValidateAlirAmount(
      cashValueTransferControl,
      wholeLifeQuote,
      wholeLifeAnnualBasePremium,
      quoteFaceAmount,
      wholeLifeGlobalErrorControl,
      clientInformation,
      this.validateExchange1035Amount.bind(this),
      maxFaceAllowed
    );

    return null;
  }

  private validateExchange1035Amount(
    annualBasePremium: number,
    wholeLifeQuote: WholeLifeQuote,
    cashValueTransferControl: AbstractControl,
    faceAmount: number,
    wholeLifeGlobalErrorControl: AbstractControl,
    clientInformation: ClientInformation,
    maxFaceAllowed?: number
  ) {
    let unscheduledAlirAmount = 0;
    if (wholeLifeQuote.exchange1035.enabled) {
      unscheduledAlirAmount = cashValueTransferControl.value;
      if (wholeLifeQuote.exchange1035.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
        const totalWlPremium = this.totalPremiumService.getTotalWholeLifePremium(cashValueTransferControl.root);
        if (totalWlPremium) {
          unscheduledAlirAmount -= totalWlPremium;
        } else {
          return; // Premium cannot be calculated, so neither can this validation
        }
      }
    }

    const validationResult = ValidatorsService.isValidAlirAmount(
      annualBasePremium,
      wholeLifeQuote.riders.rtr.enabled ? wholeLifeQuote.riders.rtr.amount : 0,
      wholeLifeQuote.product,
      null,
      faceAmount,
      unscheduledAlirAmount <= 0 ? null : unscheduledAlirAmount,
      clientInformation.dob,
      ValidatorsService.isUSCitizen(clientInformation.citizenship),
      CommonUtils.makePaymentFrequencySafeForPolicy(wholeLifeQuote.paymentFrequency),
      maxFaceAllowed
    );
    if (validationResult.isValid) {
      this.deleteError(cashValueTransferControl, 'exchange1035');
      this.deleteError(wholeLifeGlobalErrorControl, 'exchange1035');
    } else if (!validationResult.isGlobalError) {
      this.addError(cashValueTransferControl, { exchange1035: { message: validationResult.message } });
    } else {
      this.addError(wholeLifeGlobalErrorControl, {
        exchange1035: {
          message: validationResult.message,
          details: validationResult.details ? validationResult.details : undefined,
        },
      });

      // this is needed so the value is not saved for exchange1035 amount
      this.addError(cashValueTransferControl, { exchange1035: { message: '' } });
    }
  }

  wholePremiumValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    this.deleteError(control.get('wholeLifeQuote.maxPremium'), 'max');
    const { wholeLifeQuote } = control.getRawValue();
    const wholeLifeMaxPremium = control.get('wholeLifeQuote.maxPremium');
    return this._maxPremiumValidator(wholeLifeMaxPremium, wholeLifeQuote);
  };

  termPremiumValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    this.deleteError(control.get('termQuote.maxPremium'), 'max');
    const { termQuote } = control.getRawValue();
    const termQuoteMaxPremium = control.get('termQuote.maxPremium');
    return this._maxPremiumValidator(termQuoteMaxPremium, null, termQuote);
  };

  jointAppMaxPremiumValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    this.deleteError(control.get('jointAppQuote.wholeLifeQuote.maxPremium'), 'max');
    const {
      jointAppQuote: { wholeLifeQuote, termQuote },
    } = control.getRawValue();
    const wholeLifeMaxPremium = control.get('jointAppQuote.wholeLifeQuote.maxPremium');
    return this._maxPremiumValidator(wholeLifeMaxPremium, wholeLifeQuote, termQuote);
  };

  private calculateWholeLifePremium(wholeLifeQuote: WholeLifeQuote) {
    let wholePremium = 0;
    if (wholeLifeQuote.type !== POLICY_TYPES.AMEND_REMOVE_PRIMARY) {
      const premium = wholeLifeQuote.premium;
      const hasWOP = wholeLifeQuote.waiverOfPremium;
      const waiverAmount = hasWOP ? wholeLifeQuote.waiver : 0;
      const hasFlatExtras = isArray(wholeLifeQuote.flatExtras);
      const totalFlatExtras = hasFlatExtras
        ? wholeLifeQuote.flatExtras.reduce((total, fe) => total + fe.premium, 0)
        : 0;
      const hasRtr = wholeLifeQuote.riders.rtr.enabled;
      const rtrBasePremium = hasRtr ? wholeLifeQuote.riders.rtr.premium : 0;
      const rtrWaiverPremium = hasRtr && hasWOP ? wholeLifeQuote.riders.rtr.waiver : 0;
      const hasRtrFlatExtras = hasRtr && isArray(wholeLifeQuote.riders.rtr.flatExtras);
      const totalRtrFlatExtras = hasRtrFlatExtras
        ? wholeLifeQuote.riders.rtr.flatExtras.reduce((total, fe) => total + fe.premium, 0)
        : 0;
      const hasGir = wholeLifeQuote.riders.gir.enabled;
      const girBasePremium = hasGir ? wholeLifeQuote.riders.gir.premium : 0;
      const girWaiverPremium = hasGir && hasWOP ? wholeLifeQuote.riders.gir.waiver : 0;
      const hasAlir = wholeLifeQuote.riders.alir.enabled;
      const alirPremium = hasAlir ? wholeLifeQuote.riders.alir.premium : 0;

      const hasCir = wholeLifeQuote.riders.cir.enabled;
      const cirBasePremium = hasCir ? wholeLifeQuote.riders.cir.premium : 0;
      const cirWaiverPremium = hasCir ? wholeLifeQuote.riders.cir.waiver : 0;

      wholePremium =
        premium +
        waiverAmount +
        totalFlatExtras +
        rtrBasePremium +
        rtrWaiverPremium +
        totalRtrFlatExtras +
        girWaiverPremium +
        girBasePremium +
        alirPremium +
        cirBasePremium +
        cirWaiverPremium;
    }
    return wholePremium;
  }

  private _maxPremiumValidator(
    maxPremiumControl: AbstractControl,
    wholeLifeQuote?: WholeLifeQuote,
    termQuote?: TermQuote
  ): ValidationErrors | null {
    let maxPremium = 0;
    if (wholeLifeQuote) {
      maxPremium = wholeLifeQuote.maxPremium;
    } else if (termQuote) {
      maxPremium = termQuote.maxPremium;
    }
    const isAmendment = maxPremiumControl.root.get('isAmendment').value;
    if (!maxPremium || !isAmendment) {
      return null;
    }

    let termPremium = 0;
    if (termQuote && termQuote.type !== POLICY_TYPES.AMEND_REMOVE_SECONDARY) {
      if (termQuote) {
        termPremium += termQuote.premium;
        termPremium += termQuote.waiverOfPremium ? termQuote.waiver : 0;
        termPremium += termQuote.flatExtras.reduce((total, fe) => total + fe.premium, 0);
      }
    }

    let wholePremium = 0;
    if (wholeLifeQuote) {
      wholePremium = this.calculateWholeLifePremium(wholeLifeQuote);
    }

    const totalPremium = termPremium + wholePremium;
    if (totalPremium > maxPremium) {
      this.addError(maxPremiumControl, {
        max: maxPremium,
      });
    }
  }

  private async fetchPremium([
    dob,
    gender,
    healthClass,
    contractState,
    product,
    faceAmount,
    paymentFrequency,
    waiverOfPremium,
    girEnabled,
    girAmount,
    rtrEnabled,
    rtrAmount,
    cirEnabled,
    cirAmount,
    cirAccelerationPercent,
    cirMonthlyIncreaseOption,
    wholeLifeHealthClass,
  ]) {
    const faceAndRtrAmount = rtrEnabled ? faceAmount + rtrAmount : faceAmount;
    const ratingCode = wholeLifeHealthClass
      ? RateClasses[wholeLifeHealthClass].code
      : CommonUtils.determineRatingClass(healthClass, faceAndRtrAmount, product, Product.productTypes.Whole.code).code;
    const request: PremiumRequest = {
      product,
      productType: Product.productTypes.Whole.code,
      gender,
      ratingCode,
      dob,
      effectiveDate: new Date(),
      state: contractState,
      term: Product.productSpecs[product].term,
      face: faceAmount,
      flatExtras: [],
      waiver: waiverOfPremium,
      paymentFrequency,
      riders: [],
      asOfDate: new Date(),
    };
    if (girEnabled) {
      request.riders.push({ type: RiderType.GIR, amount: girAmount });
    }
    if (rtrEnabled) {
      request.riders.push({ type: RiderType.RTR, amount: rtrAmount });
    }
    if (cirEnabled) {
      request.riders.push({
        type: RiderType.CIR,
        amount: cirAmount,
        accelerationPercent: cirAccelerationPercent,
        monthlyIncreaseOption: cirMonthlyIncreaseOption,
      });
    }
    const premium = await this.controller.publicFetchProductQuote(request, this.controller.customer._id);
    return premium;
  }

  static isUSCitizen(citizenship: string): boolean {
    return citizenship === 'citizen';
  }

  isNYValidator: ValidatorFn = (contractStateControl: AbstractControl): ValidationErrors | null => {
    const value = contractStateControl.value;
    if (value !== 'NY') {
      return null;
    }
    return value ? { isNY: true } : null;
  };

  isName: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const res = this.validationService.runRule('name', control.value);
    return res.result ? null : { isInvalidName: true };
  };

  zipCodeValidator: AsyncValidatorFn = (control: AbstractControl): Observable<ValidationErrors> | null => {
    const value = control.value;
    if (!value) {
      return null;
    }
    const zipCodeValid = this.validationService.runRule('zipcode', value);

    if (!zipCodeValid.result) {
      return of({ zipCodeValid });
    }
    return from(this.controller.publicGetLocationByZip(value)).pipe(
      map((response) => {
        if (response) {
          return null;
        } else {
          return { zipCodeValid };
        }
      })
    );
  };
}
